/* eslint-disable react/no-unstable-nested-components */
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import Link from "next/link";
import { useState, type SetStateAction } from "react";
import { FormattedMessage } from "react-intl";
import { SectionJumpIdentifiers } from "components/company/SectionJumpIdentifiers";
import FullBleedButton from "components/financials/modern/FullBleedButton";
import ReportDatesRange, { getSafeFilterByReportDates } from "components/financials/modern/ReportDatesRange";
import SelectIndicatorsDataSource from "components/financials/modern/SelectIndicatorsDataSource";
import SelectStatementType from "components/financials/modern/SelectStatementType";
import SelectableArea from "components/financials/modern/SelectableArea";
import SelectedIndicatorRangePopover from "components/financials/modern/SelectedIndicatorRangePopover";
import SettingsPopover from "components/financials/modern/SettingsPopover";
import Templates from "components/financials/modern/Templates";
import { useFinancialsSheet } from "components/financials/modern/data/hooks";
import type { IFinancialsTemplateFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/FinancialsTemplateFieldsFragment.generated";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import { useTableColumns } from "components/financials/modern/hooks";
import SelectArbitraryIndicatorDebouncer from "components/financials/modern/indicators/SelectArbitraryIndicatorDebouncer";
import SelectArbitraryIndicatorPopover from "components/financials/modern/indicators/SelectArbitraryIndicatorPopover";
import SelectedIndicatorPopoverAnchor from "components/financials/modern/selection/SelectedIndicatorPopoverAnchor";
import isRangeSelectionSupported from "components/financials/modern/selection/isRangeSelectionSupported";
import type { SheetIndicator, VerticalFinancialsSheetRow } from "components/financials/modern/types";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import { ScrollArea } from "components/ui/ScrollArea";
import { SpanPixelBeacon } from "components/ui/ScrollJump";
import classNames from "components/ui/classNames";
import { useAmplitude } from "lib/amplitude/Amplitude";
import useSelfie from "lib/hooks/useSelfie";

import type { IIndicatorDataSource, ISecurityCountry } from "types/graphqlTypes";
import { IFinancialsStatementType, IIndicatorsTablePlacement, IPlatformVertical } from "types/graphqlTypes";
import SelectFinancialsCurrency from "components/financials/modern/SelectFinancialsCurrency";

interface FinancialsTableProps {
  vertical: IPlatformVertical;
  tickerSymbol: string;
  country: ISecurityCountry;
  indicatorDataSource: IIndicatorDataSource;

  arbitraryIndicators: IIndicatorFieldsFragment[];
  currency?: string;
  statementType?: IFinancialsStatementType;
  interim?: string | null;
  reportDates?: [Date | null, Date | null];
  templateId?: string | null;
  textAlignment?: "LEFT" | "CENTER" | "RIGHT";
  sortDirection?: "ASC" | "DESC";

  canChangeDataSource?: boolean;

  onChangeArbitraryIndicators: (setState: SetStateAction<IIndicatorFieldsFragment[]>) => void;
  onChangeCurrency: (currency: string) => void;
  onChangeIndicatorDataSource: (dataSource: IIndicatorDataSource) => void;
  onChangeStatementType: (statementType: IFinancialsStatementType) => void;
  onChangeInterim: (interim: string | null) => void;
  onChangeReportDates: (reportDates: [Date, Date]) => void;
  onChangeTemplate: (template: IFinancialsTemplateFieldsFragment | null) => void;
  onChangeTextAlignment: (alignment: "LEFT" | "CENTER" | "RIGHT") => void;
  onChangeSortDirection: (direction: "ASC" | "DESC") => void;
}

export default function FinancialsTable({
  vertical,
  tickerSymbol,
  country,
  statementType = IFinancialsStatementType.Annual,
  indicatorDataSource,
  interim,
  currency = "USD",
  textAlignment,
  sortDirection,
  canChangeDataSource = false,
  reportDates,
  templateId = null,
  arbitraryIndicators,
  onChangeArbitraryIndicators,
  onChangeCurrency,
  onChangeIndicatorDataSource,
  onChangeStatementType,
  onChangeInterim,
  onChangeReportDates,
  onChangeTemplate,
  onChangeTextAlignment,
  onChangeSortDirection,
}: FinancialsTableProps) {
  const [indicatorsPlacement, setIndicatorsPlacement] = useState<IIndicatorsTablePlacement>(
    IIndicatorsTablePlacement.End,
  );
  const {
    loading,
    refetch,
    rows,
    indicators,
    financialsTemplate,
    systemTemplate,
    overridingTemplate,
    renderingTemplate,
  } = useFinancialsSheet({
    tickerSymbol,
    country,
    statementType,
    currency,
    financialsTemplate: templateId,
    filterByReportDates: getSafeFilterByReportDates(reportDates),
    filterByInterim: interim || null,
    vertical,
    lastYears: 10,
    arbitraryIndicators,
    indicatorDataSource,
    indicatorsPlacement,
    sortDirection,
  });

  return (
    <Table
      vertical={vertical}
      country={country}
      tickerSymbol={tickerSymbol}
      statementType={statementType}
      currency={currency}
      interim={interim || null}
      indicatorDataSource={indicatorDataSource}
      financialsTemplate={financialsTemplate}
      renderingTemplate={renderingTemplate}
      systemTemplate={systemTemplate}
      overridingTemplate={overridingTemplate}
      indicators={indicators}
      arbitraryIndicators={arbitraryIndicators}
      textAlignment={textAlignment || "CENTER"}
      sortDirection={sortDirection || "ASC"}
      loading={loading}
      rows={rows}
      reportDates={reportDates}
      canChangeDataSource={canChangeDataSource}
      onSetArbitraryIndicators={onChangeArbitraryIndicators}
      onChangeIndicatorDataSource={onChangeIndicatorDataSource}
      onChangeIndicatorsPlacement={setIndicatorsPlacement}
      onClickRefetch={() => refetch()}
      onChangeStatementType={onChangeStatementType}
      onChangeInterim={onChangeInterim}
      onChangeCurrency={onChangeCurrency}
      onChangeReportDates={onChangeReportDates}
      onChangeTemplate={onChangeTemplate}
      onChangeTextAlignment={onChangeTextAlignment}
      onChangeSortDirection={onChangeSortDirection}
    />
  );
}

type IndicatorColumnMeta = {
  indicator: SheetIndicator;
};

interface TableProps {
  vertical: IPlatformVertical;
  country: ISecurityCountry;
  tickerSymbol: string;

  loading: boolean;
  currency: string;
  statementType: IFinancialsStatementType;
  interim: string | null;
  indicatorDataSource: IIndicatorDataSource;
  financialsTemplate: IFinancialsTemplateFieldsFragment | null;
  systemTemplate: IFinancialsTemplateFieldsFragment | null;
  overridingTemplate: IFinancialsTemplateFieldsFragment | null;
  renderingTemplate: IFinancialsTemplateFieldsFragment | null;
  rows: VerticalFinancialsSheetRow[];
  arbitraryIndicators: IIndicatorFieldsFragment[];
  reportDates?: [Date | null, Date | null];
  textAlignment: "LEFT" | "CENTER" | "RIGHT";
  sortDirection: "ASC" | "DESC";
  indicators: SheetIndicator[];

  canChangeDataSource?: boolean;

  onSetArbitraryIndicators: (setState: SetStateAction<IIndicatorFieldsFragment[]>) => void;
  onClickRefetch: () => void;
  onChangeIndicatorDataSource: (dataSource: IIndicatorDataSource) => void;
  onChangeIndicatorsPlacement: (placement: IIndicatorsTablePlacement) => void;
  onChangeCurrency: (currency: string) => void;
  onChangeStatementType: (statementType: IFinancialsStatementType) => void;
  onChangeInterim: (interim: string | null) => void;
  onChangeReportDates: (reportDates: [Date, Date]) => void;
  onChangeTemplate: (template: IFinancialsTemplateFieldsFragment | null) => void;
  onChangeTextAlignment: (alignment: "LEFT" | "CENTER" | "RIGHT") => void;
  onChangeSortDirection: (direction: "ASC" | "DESC") => void;
}

function Table({
  vertical,
  country,
  tickerSymbol,

  indicatorDataSource,
  currency,
  interim,
  statementType,
  financialsTemplate,
  renderingTemplate,
  systemTemplate,
  overridingTemplate,
  reportDates,
  textAlignment,
  sortDirection,

  loading,
  rows,
  indicators,
  arbitraryIndicators,

  canChangeDataSource,

  onClickRefetch,
  onChangeCurrency,
  onSetArbitraryIndicators,
  onChangeIndicatorDataSource,
  onChangeIndicatorsPlacement,
  onChangeStatementType,
  onChangeInterim,
  onChangeReportDates,
  onChangeTemplate,
  onChangeTextAlignment,
  onChangeSortDirection,
}: TableProps) {
  const viewer = useSelfie();
  const amplitude = useAmplitude();

  const columns = useTableColumns({
    loading,
    rows,
    indicators,
  });

  // const unavailableIndicators = indicators.filter(({ viewerCanSee }) => !viewerCanSee);
  const availableIndicators = indicators.filter(({ viewerCanSee }) => viewerCanSee);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="flex w-full flex-col space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[_1fr,_0.5fr,_1fr]">
        <div className="flex flex-col flex-wrap items-center gap-4 sm:flex-row sm:flex-nowrap">
          <div
            className={classNames("grid w-full grid-cols-2 items-center gap-4", "sm:w-auto lg:flex", {
              "grid-cols-1": !canChangeDataSource,
            })}
          >
            <SelectArbitraryIndicatorDebouncer
              selectedIndicators={arbitraryIndicators}
              onToggleIndicator={(indicator, isSelected, bestPlacement) => {
                onChangeIndicatorsPlacement(
                  bestPlacement === "START" ? IIndicatorsTablePlacement.Start : IIndicatorsTablePlacement.End,
                );
                onSetArbitraryIndicators((currentSelected) => {
                  if (!isSelected) {
                    return bestPlacement === "START"
                      ? [indicator, ...currentSelected]
                      : [...currentSelected, indicator];
                  }

                  return currentSelected.filter(({ objectId }) => objectId !== indicator.objectId);
                });
              }}
            >
              {({ selectedIndicators, onToggleIndicator }) => (
                <SelectArbitraryIndicatorPopover
                  vertical={vertical}
                  mode="TOGGLE"
                  indicatorDataSource={indicatorDataSource}
                  selectedIndicators={selectedIndicators}
                  onToggleIndicator={onToggleIndicator}
                />
              )}
            </SelectArbitraryIndicatorDebouncer>

            {canChangeDataSource && (
              <div className="flex w-full lg:hidden">
                <SelectIndicatorsDataSource
                  value={indicatorDataSource}
                  onChange={(value) => {
                    onChangeTemplate(null);
                    onChangeIndicatorDataSource(value);
                  }}
                />
              </div>
            )}
          </div>

          <Templates
            vertical={vertical}
            indicatorDataSource={indicatorDataSource}
            financialsTemplate={financialsTemplate}
            systemTemplate={systemTemplate}
            overridingTemplate={overridingTemplate}
            renderingTemplate={renderingTemplate}
            onChange={onChangeTemplate}
          />

          {vertical !== IPlatformVertical.Fiis && (
            <div className="flex w-full items-center gap-4 sm:w-auto">
              <SelectStatementType
                disabled={loading}
                interim={interim || null}
                statementType={statementType}
                onChangeStatementType={onChangeStatementType}
                onChangeInterim={onChangeInterim}
              />

              <div className="ml-auto flex space-x-4 lg:hidden">
                <SelectFinancialsCurrency
                  disabled={loading}
                  value={currency || "USD"}
                  onChange={onChangeCurrency}
                  hideChevronIcon={true}
                />

                <SettingsPopover
                  loading={loading}
                  sortDirection={sortDirection === "ASC" ? "ASC" : "DESC"}
                  textAlignment={textAlignment}
                  onChangeTextAlignment={onChangeTextAlignment}
                  onClickRefetch={onClickRefetch}
                  onToggleSorting={() => onChangeSortDirection(sortDirection === "ASC" ? "DESC" : "ASC")}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-center">
          <ReportDatesRange
            key={statementType}
            loading={loading}
            country={country}
            tickerSymbol={tickerSymbol}
            statementType={vertical === IPlatformVertical.Fiis ? IFinancialsStatementType.Monthly : statementType}
            interim={interim}
            reportDates={rows.map((row) => row.reportDate)}
            last={(() => {
              if (vertical === IPlatformVertical.Fiis) {
                return 12;
              }
              if (statementType === IFinancialsStatementType.Annual || interim) {
                return 10;
              }
              return 12;
            })()}
            limit={(() => {
              if (vertical === IPlatformVertical.Fiis) {
                return 12;
              }

              if (statementType === IFinancialsStatementType.Annual || interim) {
                return 30;
              }

              return 7;
            })()}
            disabled={loading}
            vertical={vertical}
            indicatorDataSource={indicatorDataSource}
            value={reportDates}
            onChange={onChangeReportDates}
          />
        </div>

        <div className="hidden justify-end gap-4 lg:flex">
          {canChangeDataSource && (
            <SelectIndicatorsDataSource
              value={indicatorDataSource}
              onChange={(value) => {
                onChangeTemplate(null);
                onChangeIndicatorDataSource(value);
              }}
            />
          )}

          <SelectFinancialsCurrency disabled={loading} value={currency || "USD"} onChange={onChangeCurrency} />

          <FullBleedButton />

          <SettingsPopover
            loading={loading}
            sortDirection={sortDirection === "ASC" ? "ASC" : "DESC"}
            textAlignment={textAlignment}
            onChangeTextAlignment={onChangeTextAlignment}
            onClickRefetch={onClickRefetch}
            onToggleSorting={() => onChangeSortDirection(sortDirection === "ASC" ? "DESC" : "ASC")}
          />
        </div>
      </div>

      {!loading && <SpanPixelBeacon offset={-360} id={SectionJumpIdentifiers.Financials} />}

      {availableIndicators.length === 0 && loading && <LoadingSkeleton count={1} className="h-64" />}

      {availableIndicators.length > 0 && (
        <SelectableArea indicators={indicators} sortDirection={sortDirection}>
          {({
            getSelectable,
            getSelectableClassNames,
            indicator: indicatorFromSelection,
            selection,
            isNodeSelected,
          }) => (
            <ScrollArea type="always" orientation="horizontal" className="relative h-full w-full">
              <div className="pb-4 lg:w-full">
                <div
                  className={classNames(
                    "relative overflow-auto rounded-xl border border-slate-200",
                    "dark:border-slate-950",
                  )}
                >
                  <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                    <thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr
                          key={headerGroup.id}
                          className="select-none divide-x divide-slate-200 dark:divide-slate-700 [&>th]:whitespace-nowrap"
                        >
                          {headerGroup.headers.map((header) => {
                            return (
                              <th
                                key={header.id}
                                className={classNames(
                                  "m-0 p-0 text-center",
                                  header.column.columnDef.meta?.header?.className,
                                  header.column.id === "reportDate" ? null : textAlignmentClassNames(textAlignment),
                                )}
                              >
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(header.column.columnDef.header, header.getContext())}
                              </th>
                            );
                          })}
                        </tr>
                      ))}
                    </thead>

                    <tbody className="divide-y divide-slate-200 font-mono dark:divide-slate-700">
                      {table.getRowModel().rows.map((row) => {
                        const isLastSelectedRow =
                          selection.length > 0 && selection[selection.length - 1].rowIndex === row.index;

                        return (
                          <tr key={row.id} className="group divide-x divide-gray-200 dark:divide-slate-700">
                            {row.getVisibleCells().map((cell) => {
                              const { indicator } = cell.column.columnDef.meta as IndicatorColumnMeta;
                              const isColumnSelected =
                                indicator &&
                                indicatorFromSelection &&
                                indicatorFromSelection.objectId === indicator.objectId;

                              return (
                                <td
                                  key={cell.id}
                                  className={classNames(
                                    "m-0 p-0 text-center",
                                    cell.column.columnDef.meta?.cell?.className,
                                    cell.column.id === "reportDate" ? null : textAlignmentClassNames(textAlignment),
                                  )}
                                >
                                  {indicator && isRangeSelectionSupported(indicator) ? (
                                    <div
                                      data-selectable={getSelectable(cell.row.original, cell.row.index, indicator)}
                                      data-selected={isNodeSelected(cell.row.original, cell.row.index, indicator)}
                                      className={classNames(
                                        "group",
                                        getSelectableClassNames(cell.row.original, cell.row.index, indicator),
                                      )}
                                    >
                                      {isColumnSelected ? (
                                        <SelectedIndicatorRangePopover
                                          isOpen={isColumnSelected && isLastSelectedRow}
                                          indicator={indicatorFromSelection}
                                          selection={selection}
                                        >
                                          <SelectedIndicatorPopoverAnchor>
                                            <div>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                                          </SelectedIndicatorPopoverAnchor>
                                        </SelectedIndicatorRangePopover>
                                      ) : (
                                        <div>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                                      )}
                                    </div>
                                  ) : (
                                    flexRender(cell.column.columnDef.cell, cell.getContext())
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </ScrollArea>
          )}
        </SelectableArea>
      )}

      {!viewer?.isPremium && (
        <div className="flex items-center space-x-4 rounded-xl bg-blue-50 p-2 px-4 text-center hover:opacity-80 dark:bg-blue-600/20">
          <span className="relative inline-flex h-2 w-2 md:h-2 md:w-2">
            <span
              className={classNames(
                "animate-ping-slow absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-600/50 opacity-80",
              )}
            />
            <span className={classNames("relative inline-flex h-2 w-2 rounded-full bg-blue-600 md:h-2 md:w-2")} />
          </span>

          <Link
            href="/premium"
            onClick={() => {
              amplitude.logEvent("Click on link to become Premium", {
                origin: "Financials",
              });
            }}
          >
            <p
              className={classNames(
                "rounded-xl text-sm font-medium leading-6 tracking-wide text-blue-700 dark:text-blue-300 dark:hover:text-blue-100",
              )}
            >
              <FormattedMessage defaultMessage="Para ver a tabela completa, você precisa ser Premium." id="r1ee0a" />
            </p>
          </Link>
        </div>
      )}
    </div>
  );
}

function textAlignmentClassNames(textAlignment: "LEFT" | "CENTER" | "RIGHT") {
  return classNames({
    "text-right": textAlignment === "RIGHT",
    "text-center": textAlignment === "CENTER",
    "text-left": textAlignment === "LEFT",
  });
}
